import { create } from 'zustand';
import type { RegistrationDraft } from '../types/registration';
import { emptyDraft } from '../types/registration';
import { moderate } from '../utils/mockModeration';

type DraftState = {
  draft: RegistrationDraft;

  setField: <K extends keyof RegistrationDraft>(key: K, value: RegistrationDraft[K]) => void;
  setStep: (step: RegistrationDraft['step']) => void;
  next: () => void;
  prev: () => void;
  reset: () => void;
  markReady: (isReady: boolean) => void;
  runModeration: () => Promise<boolean>;
};

/**
 * draftStore — sem persist por design.
 * Se o usuário fechar a aba no meio do registro, perde — comportamento
 * desejado pra demo (evita "draft fantasma" entre sessões).
 */
export const useDraftStore = create<DraftState>((set, get) => ({
  draft: emptyDraft(),

  setField(key, value) {
    set((state) => ({ draft: { ...state.draft, [key]: value } }));
  },

  setStep(step) {
    set((state) => ({ draft: { ...state.draft, step } }));
  },

  next() {
    set((state) => {
      const nextStep = Math.min(state.draft.step + 1, 3) as RegistrationDraft['step'];
      return { draft: { ...state.draft, step: nextStep } };
    });
  },

  prev() {
    set((state) => {
      const prevStep = Math.max(state.draft.step - 1, 1) as RegistrationDraft['step'];
      return { draft: { ...state.draft, step: prevStep } };
    });
  },

  reset() {
    set({ draft: emptyDraft() });
  },

  markReady(isReady) {
    set((state) => ({ draft: { ...state.draft, isReady } }));
  },

  async runModeration() {
    const { displayName, message } = get().draft;
    set((state) => ({
      draft: { ...state.draft, moderation: { status: 'pending' } },
    }));
    const result = await moderate({ displayName, message });
    set((state) => ({
      draft: {
        ...state.draft,
        moderation:
          result.status === 'approved'
            ? { status: 'approved' }
            : { status: 'rejected', reason: result.reason },
      },
    }));
    return result.status === 'approved';
  },
}));
